import { mapActions, mapMutations, mapState } from "vuex";
import { cloneDeep } from "@/shared";
import { serverDomain } from "@/config";
import { PLAN_NO, PLAN_TYPE, PLAN_SHOPIFY } from "@/contants/plan";
import trackEvent, { PRICING_MODEL_EVENT } from "@/utils/tracker";
import { mutations } from "@/store/downgrade/types";

export default () => ({
  computed: {
    ...mapState("dashboard", {
      dynamicContentPricing: state => state.dynamicContentPricing,
      dynamicTriggerPricing: state => state.dynamicTriggerPricing,
      isYearly: state => state.isYearly,
      currentBillingInterval: state => state.paymentInfo.type
    }),
    ...mapState("account", {
      plan: state => state.shopInfo.plan,
      planDevelopmentStore: state => state.shopInfo.planDevelopmentStore,
      account: state => state.shopInfo,
      gemVersion: state => state.shopInfo.gemVersion
    }),
    isShopifyPlanAffiliate() {
      return this.account.developmentStore && this.account.shopifyPlan === PLAN_SHOPIFY.AFFILIATE;
    }, 
    isDevelopmentStore() {
      return this.account.developmentStore
    },
    remainingDays() {
      return this.account.remainingDays;
    },
    trialExpire() {
      return (this.plan === PLAN_NO.TRIAL || this.plan === PLAN_NO.TRIAL2022) && this.account.remainingDays <= 0;
    },
    isFreeTrial() {
      return this.plan === PLAN_NO.TRIAL;
    },
    isFreeTrial2022() {
      return this.plan === PLAN_NO.TRIAL2022;
    },
    isDevelopment() {
      return this.planDevelopmentStore === PLAN_NO.DEVELOPMENT;
    },
    isDevelopment2022() {
      return this.planDevelopmentStore === PLAN_NO.DEVELOPMENT2022;
    },
    isStarter() {
      return this.plan === PLAN_NO.STARTER;
    },
    developmentStoreData() {
      if (Object.keys(this.dynamicContent).length <= 0) return;
      return this.dynamicContent?.plans?.development_store;
    },
    campaigns() {
      const isTrigger = this.triggerFrom && Object.keys(this.dynamicTriggerPricing).length > 0;
      if (isTrigger) {
        return this.dynamicTriggerPricing[this.triggerFrom].campaigns;
      }

      return this.dynamicContentPricing.campaigns;
    },
    currentPlanProfessional() {
      return this.plan === PLAN_NO.PROFESSIONAL;
    },
    currentPlanUpgrade() {
      if (this.plan === PLAN_NO.ADVANCED) {
        return PLAN_NO.OPTIMIZE;
      }

      if (this.currentPlanProfessional || this.isDevelopmentStore) {
        return PLAN_NO.BUILD;
      }

      return this.plan;
    },
    triggerFrom() {
      return this.$route.query.from;
    },
    dynamicContent() {
      let content = cloneDeep(this.dynamicContentPricing)
      const ok = this.triggerFrom && Object.keys(this.dynamicTriggerPricing).length > 0
      if (ok && this.dynamicTriggerPricing[this.triggerFrom]) {
        content = cloneDeep(this.dynamicTriggerPricing[this.triggerFrom])
      }

      if (!this.isFreeTrial) {
        if (content.plans && content.plans.trial) delete content.plans.trial;
      }

      if (!this.isStarter) {
        if (content.plans && content.plans.starter) delete content.plans.starter;
      }

      if (!this.isFreeTrial2022) {
        if (content.plans && content.plans.trial2022) delete content.plans.trial2022;
      }

      if (!this.isDevelopmentStore) {
        if (content.plans && content.plans.development_store) delete content.plans.development_store;
      }

      return content;
    },
    getFaqs() {
      if (Object.keys(this.dynamicContent).length <= 0) {
        return {
          left: [],
          right: []
        };
      }

      if ([PLAN_NO.ADVANCED, PLAN_NO.PROFESSIONAL].includes(this.plan) && !this.isDevelopmentStore) {
        return this.dynamicContent.plans[this.currentPlanUpgrade]["old_plan"].faqs;
      }

      return this.dynamicContent.plans[this.currentPlanUpgrade].faqs;
    },
    getExtraDataByCurrentPlan() {
      if (Object.keys(this.dynamicContent).length <= 0)
        return {
          page_title: "",
          page_description: ""
        };

      return this.dynamicContent.plans[this.currentPlanUpgrade]?.extra_data;
    },
    recommended() {
      const nextPlan = {
        starter: PLAN_NO.BUILD,
        trial: PLAN_NO.BUILD,
        trial2022: PLAN_NO.BUILD,
        professional: PLAN_NO.OPTIMIZE,
        advanced: PLAN_NO.ENTERPRISE,
        build: PLAN_NO.OPTIMIZE,
        optimize: PLAN_NO.ENTERPRISE
      };
      if (this.isDevelopmentStore) {
        nextPlan.build = PLAN_NO.BUILD;
        nextPlan.professional = PLAN_NO.BUILD;
      }
      
      return nextPlan[this.plan];
    },
    isTableCol4() {
      return this.isFreeTrial || this.isFreeTrial2022 || this.isStarter || this.isDevelopmentStore;
    }
  },
  methods: {
    ...mapActions("dashboard", {
      fetchDynamicContent: "fetchDynamicContent",
      fetchDynamicContentTrigger: "fetchDynamicContentTrigger"
    }),
    ...mapMutations("dashboard", {
      setBillingType: "SET_YEARLY"
    }),
    ...mapMutations("page", {
      frameLoading: "SET_FRAME_LOADING"
    }),
    ...mapMutations("downgrade", {
      toggleBlockModal: mutations.TOGGLE_BLOCK_MODAL
    }),
    chargePlan(plan, compareMode) {
      if (plan === PLAN_NO.STARTER) return;
      let url = serverDomain() + "/admin/payment/charge?plan=" + plan + "&shop=" + this.account.shopifyDomain;

      if (this.yearlyMode || compareMode) {
        url += "&type=" + PLAN_TYPE.ANNUALLY;
      }

      if ((plan === PLAN_NO.ADVANCED && this.isShowAdvandDiscount) || (plan === PLAN_NO.PROFESSIONAL && this.isShowBusinessDiscount)) {
        return url;
      }

      return url;
    },
    setYearlyMode(value) {
      this.setBillingType(value);
      try {
        trackEvent({
          eventId: PRICING_MODEL_EVENT.TOGGLE_PLAN_TYPE,
          properties: {
            type: value ? "yearly" : "monthly"
          }
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
    onChangePlan(e) {
      if (this.developmentStore) {
        return;
      }
      if (e.target) {
        const yearlyMode = Boolean(e.target.getAttribute("yearly-mode"))
        const action = e.target.getAttribute("action")
        const redirectUrl = e.target.dataset.url;
        const newPlan = e.target.dataset.plan;

        try {
          if (redirectUrl && redirectUrl.trim() !== "") {
            setTimeout(() => {
              window.location = redirectUrl;
            }, 700);
          }
          trackEvent(
            {
              eventId: PRICING_MODEL_EVENT.CHANGE_PLAN,
              properties: {
                action,
                from_plan: this.plan,
                to_plan: newPlan,
                billing_type: redirectUrl ? (redirectUrl.includes("type=annually") ? "yearly" : "monthly") : yearlyMode ? "yearly" : "monthly"
              }
            },
            () => {
              if (redirectUrl && redirectUrl.trim() !== "") {
                window.location = redirectUrl;
              }
            }
          );
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      }
    },
    onCloseSwitchModal() {
      this.openSwitchModal = false;
    },
    checkNumberFormat(num) {
      if (Number.isInteger(num)) {
        return num.toString();
      } else {
        return num.toFixed(2).toString();
      }
    }
  }
});
